/**
 * eCART scoring code vocabulary — the single source of truth shared by
 * `@agilemd/ehr-ecart` (the scoring engine + the org `/fhir` → SQS listener)
 * and `apps/hl7-processor` (post-commit scoring notifications). `ehr-ecart`
 * re-exports every symbol here, so it stays the canonical definition and
 * neither package keeps a copy.
 *
 * Mirrors `ehr-ecart`'s original `constants.js` exactly: `COMPONENT_CODES` is
 * keyed by `ALGORITHM_INPUTS` / `COMPUTATION_INPUTS` (moved here with it) so the
 * code↔input mapping reads identically to the scoring engine's.
 *
 * [BT] Order matters in COMPONENT_CODES: within each input, lower index ===
 * higher priority when observations are deduplicated. Preserve insertion order.
 */

export interface ComponentCode {
  system: string;
  code: string;
}

export const ALGORITHM_INPUTS = {
  AGE: 'age',
  ALBUMIN: 'albumin',
  ALKALINE_PHOSPHATE: 'alkalinePhosphate',
  ANION_GAP: 'anionGap',
  ASPARTATE_AMINOTRANSFERASE: 'aspartateAminotransferase',
  AVPU: 'avpu',
  BANDS: 'bands',
  BICARBONATE: 'bicarbonate',
  BLOOD_UREA_NITROGEN: 'bloodUreaNitrogen',
  BRADEN_ACTIVITY: 'bradenActivity',
  BRADEN_FRICTION: 'bradenFriction',
  BRADEN_MOBILITY: 'bradenMobility',
  BRADEN_MOISTURE: 'bradenMoisture',
  BRADEN_NUTRITION: 'bradenNutrition',
  BRADEN_SENSORY: 'bradenSensory',
  BRADEN_SCORE: 'bradenScore',
  CALCIUM: 'calcium',
  CHLORIDE: 'chloride',
  CREATININE: 'creatinine',
  DIASTOLIC_BLOOD_PRESSURE: 'diastolicBloodPressure',
  DISORIENTED: 'isDisoriented',
  EOSINOPHILS: 'eosinophils',
  FIO2: 'fiO2',
  GLUCOSE_SERUM: 'glucoseSerum',
  HAS_HAD_VENTILATOR: 'hasHadVentilator',
  HAS_VISITED_ICU: 'hasVisitedICU',
  HEART_RATE: 'heartRate',
  HEMOGLOBIN: 'hemoglobin',
  INR: 'INR',
  LACTATE: 'lactate',
  LIPASE: 'lipase',
  LYMPHOCYTES: 'lymphocytes',
  MAGNESIUM: 'magnesium',
  MCV: 'MCV',
  MONOCYTES: 'monocytes',
  NEUTROPHILS: 'neutrophils',
  OXYGEN_SATURATION: 'oxygenSaturation',
  PCO2_ARTERIAL: 'PCO2Arterial',
  PCO2_VENOUS: 'PCO2Venous',
  PHOSPHATE: 'phosphate',
  PH_ARTERIAL: 'phArterial',
  PH_VENOUS: 'phVenous',
  PLATELET_COUNT: 'plateletCount',
  PO2_ARTERIAL: 'PO2Arterial',
  POTASSIUM: 'potassium',
  PTT: 'PTT',
  RDW: 'rdw',
  RESPIRATORY_RATE: 'respiratoryRate',
  SODIUM: 'sodium',
  SYSTOLIC_BLOOD_PRESSURE: 'systolicBloodPressure',
  TEMPERATURE: 'temperature',
  TIME_OF_DAY: 'timeOfDay',
  TIME_SINCE_FIRST_VITAL_HOURS: 'timeSinceFirstVitalInHours',
  TIME_SINCE_FIRST_WARD_VITAL_HOURS: 'timeSinceFirstWardVitalInHours',
  TOTAL_BILIRUBIN: 'totalBilirubin',
  TOTAL_PROTEIN: 'totalProtein',
  WHITE_BLOOD_COUNT: 'whiteBloodCount',
  // Calculated
  ALBUMIN_DELTA: 'albuminDelta',
  ALKALINE_PHOSPHATE_DELTA: 'alkalinePhosphateDelta',
  ANION_GAP_DELTA: 'anionGapDelta',
  ASPARTATE_AMINOTRANSFERASE_DELTA: 'aspartateAminotransferaseDelta',
  AVPU_24_HOUR_MAX: 'avpu24HourMax',
  BANDS_DELTA: 'bandsDelta',
  BICARBONATE_DELTA: 'bicarbonateDelta',
  BLOOD_UREA_NITROGEN_DELTA: 'bloodUreaNitrogenDelta',
  BODY_MASS_INDEX: 'bodyMassIndex',
  BODY_MASS_INDEX_DELTA: 'bodyMassIndexDelta',
  CALCIUM_DELTA: 'calciumDelta',
  CREATININE_DELTA: 'creatinineDelta',
  DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MAX: 'diastolicBloodPressure24HourMax',
  DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MEAN: 'diastolicBloodPressure24HourMean',
  DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MIN: 'diastolicBloodPressure24HourMin',
  DIASTOLIC_BLOOD_PRESSURE_24_HOUR_SLOPE: 'diastolicBloodPressure24HourSlope',
  DIASTOLIC_BLOOD_PRESSURE_24_HOUR_STANDARD_DEVIATION:
    'diastolicBloodPressure24HourStandardDeviation',
  DISORIENTED_IN_LAST_24_HOURS: 'hasBeenDisorientedInLast24Hours',
  FIO2_24_HOUR_MAX: 'fiO224HourMax',
  FIO2_24_HOUR_MEAN: 'fiO224HourMean',
  FIO2_24_HOUR_MIN: 'fiO224HourMin',
  FIO2_24_HOUR_SLOPE: 'fiO224HourSlope',
  FIO2_24_HOUR_STANDARD_DEVIATION: 'fiO224HourStandardDeviation',
  GLUCOSE_DELTA: 'glucoseDelta',
  HEART_RATE_24_HOUR_MAX: 'heartRate24HourMax',
  HEART_RATE_24_HOUR_MEAN: 'heartRate24HourMean',
  HEART_RATE_24_HOUR_MIN: 'heartRate24HourMin',
  HEART_RATE_24_HOUR_SLOPE: 'heartRate24HourSlope',
  HEART_RATE_24_HOUR_STANDARD_DEVIATION: 'heartRate24HourStandardDeviation',
  HEMOGLOBIN_DELTA: 'hemoglobinDelta',
  LACTATE_DELTA: 'lactateDelta',
  MCV_DELTA: 'MCVDelta',
  NEUTROPHILS_DELTA: 'neutrophilsDelta',
  OXYGEN_SATURATION_24_HOUR_MIN: 'oxygenSaturation24HourMin',
  OXYGEN_SATURATION_24_HOUR_MEAN: 'oxygenSaturation24HourMean',
  OXYGEN_SATURATION_24_HOUR_MAX: 'oxygenSaturation24HourMax',
  OXYGEN_SATURATION_24_HOUR_SLOPE: 'oxygenSaturation24HourSlope',
  OXYGEN_SATURATION_24_HOUR_STANDARD_DEVIATION: 'oxygenSaturation24HourStandardDeviation',
  PHOSPHATE_DELTA: 'phosphateDelta',
  PLATELET_COUNT_DELTA: 'plateletCountDelta',
  POTASSIUM_DELTA: 'potassiumDelta',
  RESPIRATORY_RATE_24_HOUR_MAX: 'respiratoryRate24HourMax',
  RESPIRATORY_RATE_24_HOUR_MEAN: 'respiratoryRate24HourMean',
  RESPIRATORY_RATE_24_HOUR_MIN: 'respiratoryRate24HourMin',
  RESPIRATORY_RATE_24_HOUR_SLOPE: 'respiratoryRate24HourSlope',
  RESPIRATORY_RATE_24_HOUR_STANDARD_DEVIATION: 'respiratoryRate24HourStandardDeviation',
  SODIUM_DELTA: 'sodiumDelta',
  SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MAX: 'systolicBloodPressure24HourMax',
  SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MEAN: 'systolicBloodPressure24HourMean',
  SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MIN: 'systolicBloodPressure24HourMin',
  SYSTOLIC_BLOOD_PRESSURE_24_HOUR_SLOPE: 'systolicBloodPressure24HourSlope',
  SYSTOLIC_BLOOD_PRESSURE_24_HOUR_STANDARD_DEVIATION:
    'systolicBloodPressure24HourStandardDeviation',
  TEMPERATURE_24_HOUR_MAX: 'temperature24HourMax',
  TEMPERATURE_24_HOUR_MEAN: 'temperature24HourMean',
  TEMPERATURE_24_HOUR_MIN: 'temperature24HourMin',
  TEMPERATURE_24_HOUR_SLOPE: 'temperature24HourSlope',
  TEMPERATURE_24_HOUR_STANDARD_DEVIATION: 'temperature24HourStandardDeviation',
  TOTAL_BILIRUBIN_DELTA: 'totalBilirubinDelta',
  TOTAL_PROTEIN_DELTA: 'totalProteinDelta',
  URINE_OUTPUT_24_HOUR_SUM: 'urineOutput24HourSum',
  WHITE_BLOOD_COUNT_DELTA: 'whiteBloodCountDelta',
  IS_IN_EMERGENCY_DEPARTMENT: 'isInEmergencyDepartment'
} as const;

export const COMPUTATION_INPUTS = {
  HEIGHT: 'height',
  CONFUSION_ASSESSMENT_METHOD_Q3: 'confusionAssessmentMethodQ3',
  CONFUSION_ASSESSMENT_METHOD_Q4: 'confusionAssessmentMethodQ4',
  PASSERO_OPIOID_INDUCED_SEDATION_SCALE: 'passeroOpioidInducedSedationScale',
  GLASGOW_COMA_SCALE_EYE_SCORE: 'glasgowComaScaleEyeScore',
  GLASGOW_COMA_SCALE_VERBAL_SCORE: 'glasgowComaScaleVerbalScore',
  LEVEL_OF_CONSCIOUSNESS_NEMSIS: 'levelOfConsciousnessNemsis',
  LEVEL_OF_CONSCIOUSNESS_NIH: 'levelOfConsciousnessNIH',
  WEIGHT: 'weight',
  URINE_OUTPUT: 'urineOutput',
  ORIENTATION: 'orientation',
  OXYGEN_FLOW: 'oxygenFlow',
  OXYGEN_DEVICE: 'oxygenDevice',
  RAMSAY_SEDATION_SCALE: 'ramsaySedationScale',
  RICHMOND_AGITATION_SEDATION_SCALE: 'richmondAgitationSedationScale',
  DISORIENTATED: 'disorientated',
  DISORIENTATED_IN_PERSON: 'disorientatedInPerson',
  DISORIENTATED_IN_PLACE: 'disorientatedInPlace',
  DISORIENTATED_IN_TIME: 'disorientatedInTime',
  DISORIENTATED_IN_SITUATION: 'disorientatedInSituation',
  // [ZL] The following are used as inputs for computed values, but are also
  // used as normal algorithm inputs
  BICARBONATE: ALGORITHM_INPUTS.BICARBONATE,
  BRADEN_ACTIVITY: ALGORITHM_INPUTS.BRADEN_ACTIVITY,
  BRADEN_FRICTION: ALGORITHM_INPUTS.BRADEN_FRICTION,
  BRADEN_MOBILITY: ALGORITHM_INPUTS.BRADEN_MOBILITY,
  BRADEN_MOISTURE: ALGORITHM_INPUTS.BRADEN_MOISTURE,
  BRADEN_NUTRITION: ALGORITHM_INPUTS.BRADEN_NUTRITION,
  BRADEN_SENSORY: ALGORITHM_INPUTS.BRADEN_SENSORY,
  CHLORIDE: ALGORITHM_INPUTS.CHLORIDE,
  FIO2: ALGORITHM_INPUTS.FIO2,
  SODIUM: ALGORITHM_INPUTS.SODIUM,
  // [ZL] We need to include this because it is used as a basis for some unit conversions. We always need
  //      at least one WBC if it exists
  WHITE_BLOOD_COUNT: ALGORITHM_INPUTS.WHITE_BLOOD_COUNT,
  BODY_MASS_INDEX: ALGORITHM_INPUTS.BODY_MASS_INDEX,
  BRADEN_QD_SENSORY: 'bradenQdSensory',
  BRADEN_QD_MOBILITY: 'bradenQdMobility',
  BRADEN_QD_NUTRITION: 'bradenQdNutrition',
  BRADEN_QD_FRICTION: 'bradenQdFriction',
  BRADEN_QD_TOTAL: 'bradenQdTotal',
  BRADEN_SCORE: ALGORITHM_INPUTS.BRADEN_SCORE
} as const;

/**
 * Coding systems an Observation's `code` may use across the platform — the
 * single source of truth shared by the clinical-codes schema, hl7-processor,
 * and the admin-console editor.
 */
export const OBSERVATION_CODE_SYSTEMS = {
  LOINC: 'http://loinc.org',
  SNOMED: 'http://snomed.info/sct',
  AGILEMD_INTERNAL: 'urn:agilemd:fhir:observation:code-system:internal'
} as const;

export const COMPONENT_SYSTEMS = {
  LOINC: OBSERVATION_CODE_SYSTEMS.LOINC,
  SNOMED: OBSERVATION_CODE_SYSTEMS.SNOMED,
  AGILEMD_INTERNAL: OBSERVATION_CODE_SYSTEMS.AGILEMD_INTERNAL
} as const;

export const COMPONENT_CODES: Record<string, ReadonlyArray<ComponentCode>> = {
  [ALGORITHM_INPUTS.BODY_MASS_INDEX]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '39156-5' }],
  [ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '8480-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '85354-9' }
  ],
  [ALGORITHM_INPUTS.RESPIRATORY_RATE]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '9279-1' }],
  [ALGORITHM_INPUTS.TEMPERATURE]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '8310-5' }],
  [ALGORITHM_INPUTS.PLATELET_COUNT]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '97995-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '777-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26515-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '778-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '13056-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26516-5' }
  ],
  [ALGORITHM_INPUTS.OXYGEN_SATURATION]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '59408-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '20564-1' }
  ],
  [ALGORITHM_INPUTS.WHITE_BLOOD_COUNT]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '6690-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26464-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '804-5' }
  ],
  [ALGORITHM_INPUTS.GLUCOSE_SERUM]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2345-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '14749-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '74774-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77135-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '41652-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '39480-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '41651-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '39481-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '100746-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2339-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '15074-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77145-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '76629-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '54495-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50206-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50212-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50213-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50214-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50215-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50216-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50207-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50217-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50218-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '50208-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12611-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '93791-2' }
  ],
  [ALGORITHM_INPUTS.PTT]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '14979-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '3173-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '40100-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '49058-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '52123-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '43734-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '16631-4' }
  ],
  [ALGORITHM_INPUTS.MCV]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '787-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30428-7' }
  ],
  [ALGORITHM_INPUTS.HEMOGLOBIN]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '30350-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '93846-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30313-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '75928-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30351-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30352-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '59260-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '718-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '20509-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97550-8' }
  ],
  [ALGORITHM_INPUTS.LYMPHOCYTES]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '731-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26474-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '732-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '736-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26478-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '737-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30364-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30365-1' }
  ],
  [ALGORITHM_INPUTS.NEUTROPHILS]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '751-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26499-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '753-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '770-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26511-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '23761-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30451-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '768-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '32200-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26505-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '769-0' }
  ],
  [ALGORITHM_INPUTS.RDW]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '788-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30385-9' }
  ],
  [ALGORITHM_INPUTS.BLOOD_UREA_NITROGEN]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '3094-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '14937-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12962-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12961-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12963-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '6299-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '59570-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '11065-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '11064-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '48629-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '17759-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '48639-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '17760-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12964-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '72270-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12965-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '93975-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12966-8' }
  ],
  [ALGORITHM_INPUTS.MONOCYTES]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '742-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26484-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '743-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '5905-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26485-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '744-3' }
  ],
  [ALGORITHM_INPUTS.CREATININE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2160-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '14682-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77140-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '21232-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '38483-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '59826-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '72271-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '16188-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '16189-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '11042-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '51619-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '11041-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '51620-3' }
  ],
  [ALGORITHM_INPUTS.HEART_RATE]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '8867-4' }],
  [ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '8462-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '85354-9' }
  ],
  [ALGORITHM_INPUTS.BICARBONATE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2028-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77143-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '20565-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2027-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2026-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1963-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1959-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '14627-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1960-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97543-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19229-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1961-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '16551-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19223-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97545-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97544-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '48631-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '54359-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '69964-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '74684-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19230-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19231-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19232-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19233-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1962-0' }
  ],
  [ALGORITHM_INPUTS.EOSINOPHILS]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '711-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26449-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '712-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '713-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26450-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '714-6' }
  ],
  [ALGORITHM_INPUTS.ASPARTATE_AMINOTRANSFERASE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '30239-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1920-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '88112-8' }
  ],
  [ALGORITHM_INPUTS.ALKALINE_PHOSPHATE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '6768-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77141-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1783-0' }
  ],
  [ALGORITHM_INPUTS.INR]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '6301-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '38875-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '34714-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '46418-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '52129-4' }
  ],
  [ALGORITHM_INPUTS.CALCIUM]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '17861-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2000-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '42857-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1996-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '49765-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '42593-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '51950-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '59148-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '46099-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '29265-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '34907-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '18281-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '47597-0' }
  ],
  [ALGORITHM_INPUTS.POTASSIUM]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2823-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '22760-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77142-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '39789-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '32713-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '41656-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '6298-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '75940-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '39790-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '51618-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '29349-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12812-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '12813-2' }
  ],
  [ALGORITHM_INPUTS.CHLORIDE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2075-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77138-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '41649-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '41650-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '100745-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2069-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '51590-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '54369-4' }
  ],
  [ALGORITHM_INPUTS.FIO2]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '3150-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19993-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19995-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19996-8' }
  ],
  [ALGORITHM_INPUTS.LACTATE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2524-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '14118-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '32133-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '32132-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2519-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30241-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2518-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30242-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19240-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19239-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '32693-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '59032-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '51829-0' }
  ],
  [ALGORITHM_INPUTS.BRADEN_ACTIVITY]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '38228-3' }],
  [ALGORITHM_INPUTS.BRADEN_FRICTION]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '38228-3' }],
  [ALGORITHM_INPUTS.BRADEN_MOBILITY]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '38228-3' }],
  [ALGORITHM_INPUTS.BRADEN_MOISTURE]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '38228-3' }],
  [ALGORITHM_INPUTS.BRADEN_SCORE]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '38228-3' }],
  [ALGORITHM_INPUTS.BRADEN_SENSORY]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '38228-3' }],
  [ALGORITHM_INPUTS.BRADEN_NUTRITION]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '38228-3' }],
  [COMPUTATION_INPUTS.BRADEN_QD_SENSORY]: [
    {
      system: COMPONENT_SYSTEMS.AGILEMD_INTERNAL,
      code: 'braden-qd-scale-for-assessment-of-immobility-and-device-related-pressure-ulcer-risk'
    }
  ],
  [COMPUTATION_INPUTS.BRADEN_QD_MOBILITY]: [
    {
      system: COMPONENT_SYSTEMS.AGILEMD_INTERNAL,
      code: 'braden-qd-scale-for-assessment-of-immobility-and-device-related-pressure-ulcer-risk'
    }
  ],
  [COMPUTATION_INPUTS.BRADEN_QD_NUTRITION]: [
    {
      system: COMPONENT_SYSTEMS.AGILEMD_INTERNAL,
      code: 'braden-qd-scale-for-assessment-of-immobility-and-device-related-pressure-ulcer-risk'
    }
  ],
  [COMPUTATION_INPUTS.BRADEN_QD_FRICTION]: [
    {
      system: COMPONENT_SYSTEMS.AGILEMD_INTERNAL,
      code: 'braden-qd-scale-for-assessment-of-immobility-and-device-related-pressure-ulcer-risk'
    }
  ],
  [COMPUTATION_INPUTS.BRADEN_QD_TOTAL]: [
    {
      system: COMPONENT_SYSTEMS.AGILEMD_INTERNAL,
      code: 'braden-qd-scale-for-assessment-of-immobility-and-device-related-pressure-ulcer-risk'
    }
  ],
  [ALGORITHM_INPUTS.SODIUM]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2951-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77139-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '39791-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '32717-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '41657-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2947-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '39792-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '51419-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '74688-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '44783-9' }
  ],
  [ALGORITHM_INPUTS.TOTAL_PROTEIN]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '2885-2' }],
  [ALGORITHM_INPUTS.PO2_ARTERIAL]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2703-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19255-9' }
  ],
  [ALGORITHM_INPUTS.TOTAL_BILIRUBIN]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '1975-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '14631-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77137-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '59828-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '89871-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '59827-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '89872-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '42719-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '54363-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97770-2' }
  ],
  [ALGORITHM_INPUTS.ALBUMIN]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2862-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '61152-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '77148-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '76631-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '62234-0' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '61151-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '62235-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '1751-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '54347-0' }
  ],
  [ALGORITHM_INPUTS.MAGNESIUM]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '19123-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2601-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '21377-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2593-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '55937-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '65362-6' }
  ],
  [ALGORITHM_INPUTS.PCO2_ARTERIAL]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2019-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '32771-8' }
  ],
  [ALGORITHM_INPUTS.LIPASE]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '3040-3' }],
  [ALGORITHM_INPUTS.PHOSPHATE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2777-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '14879-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '2774-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '24519-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '48641-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '48617-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '55972-4' }
  ],
  [ALGORITHM_INPUTS.BANDS]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '763-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '764-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26507-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '26508-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '30229-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '35332-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '51584-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '53115-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '38518-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '71695-1' }
  ],
  [ALGORITHM_INPUTS.PH_VENOUS]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2746-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '39486-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97536-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97537-5' }
  ],
  [ALGORITHM_INPUTS.PCO2_VENOUS]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2021-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97538-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '40619-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '97539-1' }
  ],
  [ALGORITHM_INPUTS.PH_ARTERIAL]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '2744-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '33254-4' }
  ],
  [COMPUTATION_INPUTS.URINE_OUTPUT]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '79549-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '9195-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '9188-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '9187-6' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '80523-4' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '9197-5' },
    { system: COMPONENT_SYSTEMS.SNOMED, code: '251975009' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '19153-6' },
    { system: COMPONENT_SYSTEMS.SNOMED, code: '364202003' },
    { system: COMPONENT_SYSTEMS.SNOMED, code: '364201005' },
    { system: COMPONENT_SYSTEMS.SNOMED, code: '364687002' }
  ],
  [COMPUTATION_INPUTS.HEIGHT]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '8308-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '3137-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '8306-3' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '8302-2' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '8304-8' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '3138-5' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '8301-4' }
  ],
  [COMPUTATION_INPUTS.WEIGHT]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '8351-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '3141-9' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '8350-1' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '29463-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '3142-7' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '8335-2' }
  ],
  [COMPUTATION_INPUTS.GLASGOW_COMA_SCALE_EYE_SCORE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '35088-4' }
  ],
  [COMPUTATION_INPUTS.GLASGOW_COMA_SCALE_VERBAL_SCORE]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '35088-4' }
  ],
  [COMPUTATION_INPUTS.LEVEL_OF_CONSCIOUSNESS_NEMSIS]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '67775-7' }
  ],
  [COMPUTATION_INPUTS.LEVEL_OF_CONSCIOUSNESS_NIH]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '70184-7' }
  ],
  [COMPUTATION_INPUTS.OXYGEN_FLOW]: [{ system: COMPONENT_SYSTEMS.LOINC, code: '3151-8' }],
  [COMPUTATION_INPUTS.OXYGEN_DEVICE]: [
    { system: COMPONENT_SYSTEMS.AGILEMD_INTERNAL, code: 'ecart-oxygen-delivery-type' },
    { system: COMPONENT_SYSTEMS.LOINC, code: '3152-6' }
  ],
  [COMPUTATION_INPUTS.RAMSAY_SEDATION_SCALE]: [
    { system: COMPONENT_SYSTEMS.SNOMED, code: '281400004' }
  ],
  [COMPUTATION_INPUTS.RICHMOND_AGITATION_SEDATION_SCALE]: [
    { system: COMPONENT_SYSTEMS.SNOMED, code: '457441000124102' }
  ],
  [COMPUTATION_INPUTS.DISORIENTATED]: [{ system: COMPONENT_SYSTEMS.SNOMED, code: '62476001' }],
  [COMPUTATION_INPUTS.DISORIENTATED_IN_PLACE]: [
    { system: COMPONENT_SYSTEMS.SNOMED, code: '62476001' }
  ],
  [COMPUTATION_INPUTS.DISORIENTATED_IN_TIME]: [
    { system: COMPONENT_SYSTEMS.SNOMED, code: '62476001' }
  ],
  [COMPUTATION_INPUTS.DISORIENTATED_IN_SITUATION]: [
    { system: COMPONENT_SYSTEMS.SNOMED, code: '62476001' }
  ],
  [COMPUTATION_INPUTS.DISORIENTATED_IN_PERSON]: [
    { system: COMPONENT_SYSTEMS.SNOMED, code: '62476001' }
  ],
  [COMPUTATION_INPUTS.CONFUSION_ASSESSMENT_METHOD_Q3]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '95814-0' }
  ],
  [COMPUTATION_INPUTS.CONFUSION_ASSESSMENT_METHOD_Q4]: [
    { system: COMPONENT_SYSTEMS.LOINC, code: '95815-7' }
  ],
  [COMPUTATION_INPUTS.PASSERO_OPIOID_INDUCED_SEDATION_SCALE]: [
    {
      system: COMPONENT_SYSTEMS.AGILEMD_INTERNAL,
      code: 'poss-passero-opioid-induced-sedation-scale'
    }
  ]
};

/**
 * Flattened, de-duplicated set of every code in {@link COMPONENT_CODES} — the
 * "does this Observation trigger an eCART (re)score?" filter. Derived, never
 * hand-maintained, so it cannot drift from the source above.
 */
export const ECART_OBSERVATION_CODES: readonly string[] = [
  ...new Set(Object.values(COMPONENT_CODES).flatMap(pairs => pairs.map(pair => pair.code)))
];

export const ACTIVITY_TYPE = Object.freeze({
  COMMENT: 'comment',
  DISPOSITION: 'disposition',
  ENCOUNTER_STATUS: 'encounter-status',
  EXTERNAL_SOURCES: 'external-sources',
  DEMOGRAPHICS: 'demographic-data',
  RRT_NOTE: 'rrt-note',
  TRANSFER: 'transfer',
  VENTILATOR: 'ventilator',
  VERIFY_COMPONENT: 'verify-component'
});
export const ACTIVITY_TYPES = Object.values(ACTIVITY_TYPE);
export const ACTIVITY_TYPE_LABEL_MAP = Object.freeze({
  [ACTIVITY_TYPE.COMMENT]: 'Comment',
  [ACTIVITY_TYPE.DISPOSITION]: 'Disposition',
  [ACTIVITY_TYPE.ENCOUNTER_STATUS]: 'Admit / Discharge',
  [ACTIVITY_TYPE.EXTERNAL_SOURCES]: 'Pathway responses',
  [ACTIVITY_TYPE.DEMOGRAPHICS]: 'Demographic data',
  [ACTIVITY_TYPE.RRT_NOTE]: 'RRT note',
  [ACTIVITY_TYPE.TRANSFER]: 'Transfer',
  [ACTIVITY_TYPE.VENTILATOR]: 'Ventilator',
  [ACTIVITY_TYPE.VERIFY_COMPONENT]: 'Verified component'
});

export const DESTINATION_TYPES = Object.freeze({
  CERNER_EVENT_CODE: 'cerner-event-code',
  EPIC_FLOWSHEET: 'epic-flowsheet',
  ORGANIZATION_CUSTOM: 'organization-custom',
  TEMPLATE: 'fhir-template'
});

export const ECART_RISKS = Object.freeze({
  HIGH: 'high',
  MODERATE: 'moderate',
  AVERAGE: 'average'
} as const);

export type EcartRisk = (typeof ECART_RISKS)[keyof typeof ECART_RISKS];

export const MODEL_VERSIONS = {
  V2: '2',
  // [ZL] v4 is no longer a configurable scoring version; it stays here because stored v4
  //      scores are still read, categorized and displayed
  V4: '4',
  V5: '5'
} as const;

export type MajorModelVersion = (typeof MODEL_VERSIONS)[keyof typeof MODEL_VERSIONS];

// Separate from MODEL_VERSIONS because that enum's V2 is the deprecated adult eCART v2,
// not a pCART version.
export const PCART_MODEL_VERSIONS = {
  V1: '1',
  V2: '2'
} as const;

export type PcartMajorModelVersion =
  (typeof PCART_MODEL_VERSIONS)[keyof typeof PCART_MODEL_VERSIONS];

export const DISPOSITION_ENTRY_METHODS = Object.freeze({
  IN_APP: 'in-app',
  IN_PATHWAY: 'pathway',
  DISABLED: 'none'
} as const);

export type DispositionEntryMethod =
  (typeof DISPOSITION_ENTRY_METHODS)[keyof typeof DISPOSITION_ENTRY_METHODS];

export const ECART = Object.freeze({
  DEFAULT_TIMESPAN_LIST: '24h',
  DEFAULT_TIMESPAN_LIST_RETROSPECTIVE: '72h',
  DEFAULT_TIMESPAN_DETAIL: '72h',
  PATIENT_ENCOUNTER: 'patient-encounter',
  X_HIGH_COMPONENT: 'xhigh',
  HIGH_COMPONENT: 'high',
  NORMAL_COMPONENT: 'normal',
  ABNORMAL_COMPONENT: 'abnormal',
  CRITICAL_COMPONENT: 'critical',
  LOW_COMPONENT: 'low',
  X_LOW_COMPONENT: 'xlow',
  OUT_OF_RANGE_COMPONENT: 'err',
  INVALID_COMPONENT: 'invalid',
  USER_VERIFIED_ABNORMAL_COMPONENT: 'user-verified-abnormal',
  SORT_LOCATION_ASC: 'location-asc',
  SORT_LOCATION_DESC: 'location-desc',
  SORT_NAME_ASC: 'name-asc',
  SORT_NAME_DESC: 'name-desc',
  SORT_PERCENTILE_ASC: 'percentile-asc',
  SORT_PERCENTILE_DESC: 'percentile-desc',
  SORT_RRT_NOTE_ASC: 'rttnote-asc',
  SORT_RRT_NOTE_DESC: 'rttnote-desc',
  SORT_TIME_DISCHARGED_ASC: 'date-asc',
  SORT_TIME_DISCHARGED_DESC: 'date-desc',
  RISK_HIGH: ECART_RISKS.HIGH,
  RISK_MODERATE: ECART_RISKS.MODERATE,
  RISK_AVERAGE: ECART_RISKS.AVERAGE,
  RISK_OPTIONS: [
    { value: ECART_RISKS.HIGH, label: 'High' },
    { value: ECART_RISKS.MODERATE, label: 'Moderate' },
    { value: ECART_RISKS.AVERAGE, label: 'Average' }
  ]
});

export const ECART_ALGORITHMS = Object.freeze({
  ECART: 'ecart',
  PCART: 'pcart'
} as const);

export type EcartAlgorithm = (typeof ECART_ALGORITHMS)[keyof typeof ECART_ALGORITHMS];

/** RiskAssessment status per eCART case: FINAL is the historical ("initial") case, AMENDED the live one. */
export const ECART_CASE_STATUS = Object.freeze({
  FINAL: 'final',
  AMENDED: 'amended'
} as const);

export type EcartCaseStatus = (typeof ECART_CASE_STATUS)[keyof typeof ECART_CASE_STATUS];

export const ECART_METHOD_CODING = Object.freeze({
  V4: Object.freeze({
    system: 'urn:agilemd:fhir:risk-assessment:method:agilemd-internal',
    code: 'ecart-v4'
  } as const),
  DEFAULT: Object.freeze({
    system: 'urn:agilemd:fhir:risk-assessment:method:agilemd-internal',
    code: 'ecart'
  } as const)
});

/** What an eCART score input's `inputMetadata[input].sources[]` entry points at. */
export const ECART_INPUT_SOURCE_TYPES = Object.freeze({
  OBSERVATION: 'observation'
} as const);

export const ECART_CASE_SYSTEMS = Object.freeze({
  BED: 'urn:agilemd:fhir::tag-system:bed',
  CASE_ACTIVE: 'urn:agilemd:fhir::tag-system:case-active',
  CASE_SCORING_ON_VENT: 'urn:agilemd:fhir::tag-system:case-scoring-on-vent',
  CASE_EXCLUDED: 'urn:agilemd:fhir::tag-system:case-excluded',
  EXCLUSION_REASONS: 'urn:agilemd:fhir::tag-system:case-exclusion-reasons',
  DISPOSITION: 'urn:agilemd:fhir::tag-system:ecart-disposition',
  FACILITY_ID: 'urn:agilemd:fhir::tag-system:facility-id',
  LAST_KNOWN_PATIENT_BIRTHDATE: 'urn:agilemd:fhir::tag-system:last-known-patient-birthdate',
  LIVE_CASE_ID: 'urn:agilemd:fhir::tag-system:related-live-assessment',
  HISTORICAL_CASE_ID: 'urn:agilemd:fhir::tag-system:related-historical-assessment',
  OUT_OF_RANGE_COMPONENT: 'urn:agilemd:fhir::tag-system:agilemd-ecart-out-of-range-component',
  USER_VERIFIED_ABNORMAL_COMPONENT:
    'urn:agilemd:fhir::tag-system:agilemd-ecart-user-verified-abnormal-component',
  QUALITATIVE_RISK: 'urn:agilemd:fhir:risk-assessment:qualitatve-risk:ecart-v4',
  ROOM: 'urn:agilemd:fhir::tag-system:room',
  TIME_LAST_UPDATED: 'urn:agilemd:fhir::tag-system:time-last-updated',
  UNIT: 'urn:agilemd:fhir::tag-system:unit',
  LOCATION_CLASSIFICATION: 'urn:agilemd:fhir::tag-system:ecart-location-classification',
  CASE_ECART_SCORING_UNDER_18: 'urn:agilemd:fhir::tag-system:case-ecart-scoring-under-18'
} as const);

export const ENCOUNTER_STATUS_TYPES = Object.freeze({
  'in-progress': 'Admitted',
  finished: 'Discharged',
  planned: 'Planned admission'
});

export const FHIR_SYSTEMS = Object.freeze({
  ACT_ENCOUNTER_CODE: 'http://hl7.org/fhir/v3/ActCode',
  ENCOUNTER_ID: 'urn:agilemd:fhir:encounter:identifier-system:id',
  PATIENT_EPT_ID: 'urn:agilemd:fhir:patient:identifier-system:ept-id',
  PATIENT_ENTERPRISE_ID: 'urn:agilemd:fhir:patient:identifier-system:enterprise-id',
  PATIENT_PERSON_ID: 'urn:agilemd:fhir:patient:identifier-system:person-id',
  PRACTITIONER_USER_ID: 'urn:agilemd:fhir:practitioner:identifier-system:user-id',
  SESSION_ID: 'urn:agilemd:fhir::tag-system:agilemd-session-id',
  LOCATION: {
    FACILITY_TAG: 'urn:agilemd:fhir:location:tag-system:facility',
    TYPE_TAG: 'urn:agilemd:fhir:location:type-system:default',
    UNIT_TAG: 'urn:agilemd:fhir:location:tag-system:point-of-care',
    ROOM_TAG: 'urn:agilemd:fhir:location:tag-system:room',
    BED_TAG: 'urn:agilemd:fhir:location:tag-system:bed'
  }
});

export const USES = Object.freeze({
  USUAL: 'usual',
  KIOSK: 'kiosk'
});

export const OBSERVATION_INTERPRETATIONS = {
  CRITICALLY_ABNORMAL: 'AA'
};

export const COMPONENT_WEIGHT_CATEGORY_THRESHOLDS = {
  // greater than this value is considered to be a heavily weighted component
  LARGE: 0.01,
  // greater than this value is considered to be a medium weighted component
  MEDIUM: 0.005,
  // greater than this value is considered to be a low weighted component
  // less than this value is considered to be a very low weighted component
  SMALL: 0.002
};

/** The computation parameters an input's trend view exposes as its own sources. */
export const ALGORITHM_COMPUTED_INPUTS_PARAMS = Object.freeze({
  FIO2_OXYGEN_DEVICE: COMPUTATION_INPUTS.OXYGEN_DEVICE,
  FIO2_OXYGEN_FLOW: COMPUTATION_INPUTS.OXYGEN_FLOW
});

/** Not an algorithm input — the paired systolic/diastolic view the UI groups them under. */
export const BLOOD_PRESSURE = 'bloodPressure';

// The adult subsets below are denylists over every ALGORITHM_INPUTS value, so an input
// added for another model reaches eCART unless it is named here.
const PCART_V2_ONLY_ALGORITHM_INPUTS: readonly string[] = [
  ALGORITHM_INPUTS.ALBUMIN_DELTA,
  ALGORITHM_INPUTS.ALKALINE_PHOSPHATE_DELTA,
  ALGORITHM_INPUTS.ASPARTATE_AMINOTRANSFERASE_DELTA,
  ALGORITHM_INPUTS.BANDS_DELTA,
  ALGORITHM_INPUTS.CALCIUM_DELTA,
  ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MEAN,
  ALGORITHM_INPUTS.LACTATE_DELTA,
  ALGORITHM_INPUTS.MCV_DELTA,
  ALGORITHM_INPUTS.NEUTROPHILS_DELTA,
  ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_MAX,
  ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_SLOPE,
  ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_STANDARD_DEVIATION,
  ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_MIN,
  ALGORITHM_INPUTS.SODIUM_DELTA,
  ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MAX,
  ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MEAN,
  ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MIN,
  ALGORITHM_INPUTS.TOTAL_BILIRUBIN_DELTA,
  ALGORITHM_INPUTS.TOTAL_PROTEIN_DELTA
];

export const ADULT_ALGORITHM_INPUTS_SUBSET_V5 = Object.values(ALGORITHM_INPUTS).filter(
  i =>
    i !== ALGORITHM_INPUTS.TIME_SINCE_FIRST_WARD_VITAL_HOURS &&
    i !== ALGORITHM_INPUTS.HAS_HAD_VENTILATOR &&
    !PCART_V2_ONLY_ALGORITHM_INPUTS.includes(i)
);
export const PEDIATRIC_ALGORITHM_INPUTS_SUBSET = [
  ALGORITHM_INPUTS.AGE,
  ALGORITHM_INPUTS.ALBUMIN,
  ALGORITHM_INPUTS.ALKALINE_PHOSPHATE,
  ALGORITHM_INPUTS.ANION_GAP,
  ALGORITHM_INPUTS.ASPARTATE_AMINOTRANSFERASE,
  ALGORITHM_INPUTS.AVPU,
  ALGORITHM_INPUTS.BANDS,
  ALGORITHM_INPUTS.TOTAL_BILIRUBIN,
  ALGORITHM_INPUTS.BLOOD_UREA_NITROGEN,
  ALGORITHM_INPUTS.CALCIUM,
  ALGORITHM_INPUTS.CREATININE,
  ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE,
  ALGORITHM_INPUTS.FIO2,
  ALGORITHM_INPUTS.GLUCOSE_SERUM,
  ALGORITHM_INPUTS.BICARBONATE,
  ALGORITHM_INPUTS.HEMOGLOBIN,
  ALGORITHM_INPUTS.HEART_RATE,
  ALGORITHM_INPUTS.LACTATE,
  ALGORITHM_INPUTS.MCV,
  ALGORITHM_INPUTS.NEUTROPHILS,
  ALGORITHM_INPUTS.OXYGEN_SATURATION,
  ALGORITHM_INPUTS.PHOSPHATE,
  ALGORITHM_INPUTS.PLATELET_COUNT,
  ALGORITHM_INPUTS.POTASSIUM,
  ALGORITHM_INPUTS.RESPIRATORY_RATE,
  ALGORITHM_INPUTS.TOTAL_PROTEIN,
  ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE,
  ALGORITHM_INPUTS.SODIUM,
  ALGORITHM_INPUTS.TEMPERATURE,
  ALGORITHM_INPUTS.TIME_SINCE_FIRST_WARD_VITAL_HOURS,
  ALGORITHM_INPUTS.WHITE_BLOOD_COUNT,
  ALGORITHM_INPUTS.IS_IN_EMERGENCY_DEPARTMENT
];

// The 86 predictors of the pCARTv2 PMML. Same base inputs as v1 plus their 24-hour
// aggregations and lab deltas; v1's isInEmergencyDepartment is not a v2 predictor.
export const PEDIATRIC_ALGORITHM_INPUTS_SUBSET_V2 = [
  ALGORITHM_INPUTS.AGE,
  ALGORITHM_INPUTS.ALBUMIN,
  ALGORITHM_INPUTS.ALKALINE_PHOSPHATE,
  ALGORITHM_INPUTS.ANION_GAP,
  ALGORITHM_INPUTS.ASPARTATE_AMINOTRANSFERASE,
  ALGORITHM_INPUTS.AVPU,
  ALGORITHM_INPUTS.BANDS,
  ALGORITHM_INPUTS.BICARBONATE,
  ALGORITHM_INPUTS.BLOOD_UREA_NITROGEN,
  ALGORITHM_INPUTS.CALCIUM,
  ALGORITHM_INPUTS.CREATININE,
  ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE,
  ALGORITHM_INPUTS.FIO2,
  ALGORITHM_INPUTS.GLUCOSE_SERUM,
  ALGORITHM_INPUTS.HEART_RATE,
  ALGORITHM_INPUTS.HEMOGLOBIN,
  ALGORITHM_INPUTS.LACTATE,
  ALGORITHM_INPUTS.MCV,
  ALGORITHM_INPUTS.NEUTROPHILS,
  ALGORITHM_INPUTS.OXYGEN_SATURATION,
  ALGORITHM_INPUTS.PHOSPHATE,
  ALGORITHM_INPUTS.PLATELET_COUNT,
  ALGORITHM_INPUTS.POTASSIUM,
  ALGORITHM_INPUTS.RESPIRATORY_RATE,
  ALGORITHM_INPUTS.SODIUM,
  ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE,
  ALGORITHM_INPUTS.TEMPERATURE,
  ALGORITHM_INPUTS.TIME_SINCE_FIRST_WARD_VITAL_HOURS,
  ALGORITHM_INPUTS.TOTAL_BILIRUBIN,
  ALGORITHM_INPUTS.TOTAL_PROTEIN,
  ALGORITHM_INPUTS.WHITE_BLOOD_COUNT,
  ALGORITHM_INPUTS.ALBUMIN_DELTA,
  ALGORITHM_INPUTS.ALKALINE_PHOSPHATE_DELTA,
  ALGORITHM_INPUTS.ANION_GAP_DELTA,
  ALGORITHM_INPUTS.ASPARTATE_AMINOTRANSFERASE_DELTA,
  ALGORITHM_INPUTS.BANDS_DELTA,
  ALGORITHM_INPUTS.BLOOD_UREA_NITROGEN_DELTA,
  ALGORITHM_INPUTS.CALCIUM_DELTA,
  ALGORITHM_INPUTS.CREATININE_DELTA,
  ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MAX,
  ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MEAN,
  ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MIN,
  ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_SLOPE,
  ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_STANDARD_DEVIATION,
  ALGORITHM_INPUTS.FIO2_24_HOUR_MAX,
  ALGORITHM_INPUTS.FIO2_24_HOUR_MEAN,
  ALGORITHM_INPUTS.FIO2_24_HOUR_MIN,
  ALGORITHM_INPUTS.FIO2_24_HOUR_SLOPE,
  ALGORITHM_INPUTS.FIO2_24_HOUR_STANDARD_DEVIATION,
  ALGORITHM_INPUTS.GLUCOSE_DELTA,
  ALGORITHM_INPUTS.HEART_RATE_24_HOUR_MAX,
  ALGORITHM_INPUTS.HEART_RATE_24_HOUR_MEAN,
  ALGORITHM_INPUTS.HEART_RATE_24_HOUR_MIN,
  ALGORITHM_INPUTS.HEART_RATE_24_HOUR_SLOPE,
  ALGORITHM_INPUTS.HEART_RATE_24_HOUR_STANDARD_DEVIATION,
  ALGORITHM_INPUTS.HEMOGLOBIN_DELTA,
  ALGORITHM_INPUTS.LACTATE_DELTA,
  ALGORITHM_INPUTS.MCV_DELTA,
  ALGORITHM_INPUTS.NEUTROPHILS_DELTA,
  ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_MAX,
  ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_MEAN,
  ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_MIN,
  ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_SLOPE,
  ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_STANDARD_DEVIATION,
  ALGORITHM_INPUTS.PHOSPHATE_DELTA,
  ALGORITHM_INPUTS.PLATELET_COUNT_DELTA,
  ALGORITHM_INPUTS.POTASSIUM_DELTA,
  ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_MAX,
  ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_MEAN,
  ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_MIN,
  ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_SLOPE,
  ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_STANDARD_DEVIATION,
  ALGORITHM_INPUTS.SODIUM_DELTA,
  ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MAX,
  ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MEAN,
  ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MIN,
  ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_SLOPE,
  ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_STANDARD_DEVIATION,
  ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MAX,
  ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MEAN,
  ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MIN,
  ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_SLOPE,
  ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_STANDARD_DEVIATION,
  ALGORITHM_INPUTS.TOTAL_BILIRUBIN_DELTA,
  ALGORITHM_INPUTS.TOTAL_PROTEIN_DELTA,
  ALGORITHM_INPUTS.WHITE_BLOOD_COUNT_DELTA
];

export const ALGORITHM_INPUTS_TO_FETCH_OBSERVATIONS = [ALGORITHM_INPUTS.FIO2];

export interface AlgorithmGroup {
  label: string;
  values: string[];
}

export interface AlgorithmSubgroupEntry {
  label: string;
  value: string;
}

export interface AlgorithmSubgroup {
  label: string;
  trend?: string | string[];
  sources?: AlgorithmSubgroupEntry[];
  groups?: AlgorithmSubgroupEntry[];
}

export const ALGORITHM_GROUPING: AlgorithmGroup[] = [
  {
    label: 'Assessments',
    values: [
      ALGORITHM_INPUTS.TEMPERATURE,
      ALGORITHM_INPUTS.HEART_RATE,
      BLOOD_PRESSURE,
      ALGORITHM_INPUTS.RESPIRATORY_RATE,
      ALGORITHM_INPUTS.OXYGEN_SATURATION,
      ALGORITHM_INPUTS.FIO2,
      ALGORITHM_INPUTS.BODY_MASS_INDEX,
      ALGORITHM_INPUTS.URINE_OUTPUT_24_HOUR_SUM,
      ALGORITHM_INPUTS.AVPU,
      ALGORITHM_INPUTS.BRADEN_SCORE
    ]
  },
  {
    label: 'Hematology',
    values: [
      ALGORITHM_INPUTS.WHITE_BLOOD_COUNT,
      ALGORITHM_INPUTS.HEMOGLOBIN,
      ALGORITHM_INPUTS.PLATELET_COUNT
    ]
  },
  {
    label: 'Coagulation',
    values: [ALGORITHM_INPUTS.PTT, ALGORITHM_INPUTS.INR]
  },
  {
    label: 'Chemistry',
    values: [
      ALGORITHM_INPUTS.GLUCOSE_SERUM,
      ALGORITHM_INPUTS.SODIUM,
      ALGORITHM_INPUTS.POTASSIUM,
      ALGORITHM_INPUTS.CHLORIDE,
      ALGORITHM_INPUTS.BICARBONATE,
      ALGORITHM_INPUTS.ANION_GAP,
      ALGORITHM_INPUTS.BLOOD_UREA_NITROGEN,
      ALGORITHM_INPUTS.CREATININE,
      ALGORITHM_INPUTS.CALCIUM,
      ALGORITHM_INPUTS.MAGNESIUM,
      ALGORITHM_INPUTS.PHOSPHATE,
      ALGORITHM_INPUTS.TOTAL_PROTEIN,
      ALGORITHM_INPUTS.ALBUMIN,
      ALGORITHM_INPUTS.TOTAL_BILIRUBIN,
      ALGORITHM_INPUTS.ALKALINE_PHOSPHATE,
      ALGORITHM_INPUTS.ASPARTATE_AMINOTRANSFERASE,
      ALGORITHM_INPUTS.LACTATE,
      ALGORITHM_INPUTS.LIPASE
    ]
  },
  {
    label: 'Blood Gas',
    values: [
      ALGORITHM_INPUTS.PH_ARTERIAL,
      ALGORITHM_INPUTS.PCO2_ARTERIAL,
      ALGORITHM_INPUTS.PO2_ARTERIAL,
      ALGORITHM_INPUTS.PH_VENOUS,
      ALGORITHM_INPUTS.PCO2_VENOUS
    ]
  },
  {
    label: 'Other',
    values: [
      ALGORITHM_INPUTS.AGE,
      ALGORITHM_INPUTS.TIME_SINCE_FIRST_VITAL_HOURS,
      ALGORITHM_INPUTS.TIME_SINCE_FIRST_WARD_VITAL_HOURS,
      ALGORITHM_INPUTS.HAS_VISITED_ICU,
      ALGORITHM_INPUTS.HAS_HAD_VENTILATOR,
      ALGORITHM_INPUTS.TIME_OF_DAY
    ]
  }
];

export const ALGORITHM_SUBGROUPS: Record<string, AlgorithmSubgroup> = {
  [ALGORITHM_INPUTS.TEMPERATURE]: {
    label: 'Temperature trends (24hrs)',
    trend: ALGORITHM_INPUTS.TEMPERATURE,
    groups: [
      {
        label: 'Maximum',
        value: ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MAX
      },
      {
        label: 'Mean',
        value: ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MEAN
      },
      {
        label: 'Minimum',
        value: ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_MIN
      },
      {
        label: 'Standard deviation (°C)',
        value: ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_STANDARD_DEVIATION
      },
      {
        label: 'Slope (°C)',
        value: ALGORITHM_INPUTS.TEMPERATURE_24_HOUR_SLOPE
      }
    ]
  },
  [ALGORITHM_INPUTS.HEART_RATE]: {
    label: 'Heart Rate (beats/min)',
    trend: ALGORITHM_INPUTS.HEART_RATE,
    groups: [
      {
        label: 'Maximum',
        value: ALGORITHM_INPUTS.HEART_RATE_24_HOUR_MAX
      },
      {
        label: 'Mean',
        value: ALGORITHM_INPUTS.HEART_RATE_24_HOUR_MEAN
      },
      {
        label: 'Minimum',
        value: ALGORITHM_INPUTS.HEART_RATE_24_HOUR_MIN
      },
      {
        label: 'Standard deviation',
        value: ALGORITHM_INPUTS.HEART_RATE_24_HOUR_STANDARD_DEVIATION
      },
      {
        label: 'Slope',
        value: ALGORITHM_INPUTS.HEART_RATE_24_HOUR_SLOPE
      }
    ]
  },
  [BLOOD_PRESSURE]: {
    label: 'Blood pressure trend (24 hrs)',
    trend: [ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE, ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE]
  },
  [ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE]: {
    label: 'Systolic Blood Pressure',
    groups: [
      {
        label: 'Maximum',
        value: ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MAX
      },
      {
        label: 'Mean',
        value: ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MEAN
      },
      {
        label: 'Minimum',
        value: ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_MIN
      },
      {
        label: 'Standard Deviation',
        value: ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_STANDARD_DEVIATION
      },
      {
        label: 'Slope',
        value: ALGORITHM_INPUTS.SYSTOLIC_BLOOD_PRESSURE_24_HOUR_SLOPE
      }
    ]
  },
  [ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE]: {
    label: 'Diastolic Blood Pressure',
    groups: [
      {
        label: 'Maximum',
        value: ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MAX
      },
      {
        label: 'Mean',
        value: ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MEAN
      },
      {
        label: 'Minimum',
        value: ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_MIN
      },
      {
        label: 'Standard Deviation',
        value: ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_STANDARD_DEVIATION
      },
      {
        label: 'Slope',
        value: ALGORITHM_INPUTS.DIASTOLIC_BLOOD_PRESSURE_24_HOUR_SLOPE
      }
    ]
  },
  [ALGORITHM_INPUTS.RESPIRATORY_RATE]: {
    label: 'Respiration trend (24 hrs)',
    trend: ALGORITHM_INPUTS.RESPIRATORY_RATE,
    groups: [
      {
        label: 'Maximum',
        value: ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_MAX
      },
      {
        label: 'Mean',
        value: ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_MEAN
      },
      {
        label: 'Minimum',
        value: ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_MIN
      },
      {
        label: 'Standard Deviation',
        value: ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_STANDARD_DEVIATION
      },
      {
        label: 'Slope',
        value: ALGORITHM_INPUTS.RESPIRATORY_RATE_24_HOUR_SLOPE
      }
    ]
  },
  [ALGORITHM_INPUTS.OXYGEN_SATURATION]: {
    label: 'Oxygen Saturation trend (24hrs)',
    trend: ALGORITHM_INPUTS.OXYGEN_SATURATION,
    groups: [
      {
        label: 'Maximum',
        value: ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_MAX
      },
      {
        label: 'Minimum',
        value: ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_MIN
      },
      {
        label: 'Mean',
        value: ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_MEAN
      },
      {
        label: 'Standard Deviation',
        value: ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_STANDARD_DEVIATION
      },
      {
        label: 'Slope',
        value: ALGORITHM_INPUTS.OXYGEN_SATURATION_24_HOUR_SLOPE
      }
    ]
  },
  [ALGORITHM_INPUTS.FIO2]: {
    label: 'FiO2 trend (24hrs)',
    trend: ALGORITHM_INPUTS.FIO2,
    sources: [
      {
        label: 'O₂ Device',
        value: ALGORITHM_COMPUTED_INPUTS_PARAMS.FIO2_OXYGEN_DEVICE
      },
      {
        label: 'O₂ Flow (LPM)',
        value: ALGORITHM_COMPUTED_INPUTS_PARAMS.FIO2_OXYGEN_FLOW
      }
    ],
    groups: [
      {
        label: 'Maximum',
        value: ALGORITHM_INPUTS.FIO2_24_HOUR_MAX
      },
      {
        label: 'Mean',
        value: ALGORITHM_INPUTS.FIO2_24_HOUR_MEAN
      },
      {
        label: 'Minimum',
        value: ALGORITHM_INPUTS.FIO2_24_HOUR_MIN
      },
      {
        label: 'Standard Deviation',
        value: ALGORITHM_INPUTS.FIO2_24_HOUR_STANDARD_DEVIATION
      },
      {
        label: 'Slope',
        value: ALGORITHM_INPUTS.FIO2_24_HOUR_SLOPE
      }
    ]
  },
  [ALGORITHM_INPUTS.BODY_MASS_INDEX]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.BODY_MASS_INDEX_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.BRADEN_SCORE]: {
    label: 'Braden Scale sub-scores',
    groups: [
      { label: 'Sensory', value: ALGORITHM_INPUTS.BRADEN_SENSORY },
      { label: 'Moisture', value: ALGORITHM_INPUTS.BRADEN_MOISTURE },
      { label: 'Activity', value: ALGORITHM_INPUTS.BRADEN_ACTIVITY },
      { label: 'Mobility', value: ALGORITHM_INPUTS.BRADEN_MOBILITY },
      { label: 'Nutrition', value: ALGORITHM_INPUTS.BRADEN_NUTRITION },
      { label: 'Friction', value: ALGORITHM_INPUTS.BRADEN_FRICTION }
    ]
  },
  [ALGORITHM_INPUTS.AVPU]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Least responsiveness, 24hrs',
        value: ALGORITHM_INPUTS.AVPU_24_HOUR_MAX
      },
      {
        label: 'Disoriented',
        value: ALGORITHM_INPUTS.DISORIENTED
      },
      {
        label: 'Ever disoriented,  24hrs',
        value: ALGORITHM_INPUTS.DISORIENTED_IN_LAST_24_HOURS
      }
    ]
  },
  [ALGORITHM_INPUTS.WHITE_BLOOD_COUNT]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.WHITE_BLOOD_COUNT_DELTA
      },
      {
        label: 'Neutrophils (%)',
        value: ALGORITHM_INPUTS.NEUTROPHILS
      },
      {
        label: 'Neutrophils change from prior',
        value: ALGORITHM_INPUTS.NEUTROPHILS_DELTA
      },
      {
        label: 'Bands (%)',
        value: ALGORITHM_INPUTS.BANDS
      },
      {
        label: 'Bands change from prior',
        value: ALGORITHM_INPUTS.BANDS_DELTA
      },
      {
        label: 'Lymphocytes (%)',
        value: ALGORITHM_INPUTS.LYMPHOCYTES
      },
      {
        label: 'Monocytes (%)',
        value: ALGORITHM_INPUTS.MONOCYTES
      },
      {
        label: 'Eosinophils (%)',
        value: ALGORITHM_INPUTS.EOSINOPHILS
      }
    ]
  },
  [ALGORITHM_INPUTS.HEMOGLOBIN]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.HEMOGLOBIN_DELTA
      },
      {
        label: 'MCV (fL)',
        value: ALGORITHM_INPUTS.MCV
      },
      {
        label: 'MCV change from prior',
        value: ALGORITHM_INPUTS.MCV_DELTA
      },
      {
        label: 'RDW (%)',
        value: ALGORITHM_INPUTS.RDW
      }
    ]
  },
  [ALGORITHM_INPUTS.PLATELET_COUNT]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.PLATELET_COUNT_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.GLUCOSE_SERUM]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.GLUCOSE_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.POTASSIUM]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.POTASSIUM_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.BICARBONATE]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.BICARBONATE_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.ANION_GAP]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.ANION_GAP_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.BLOOD_UREA_NITROGEN]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.BLOOD_UREA_NITROGEN_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.CREATININE]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.CREATININE_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.ALBUMIN]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.ALBUMIN_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.ALKALINE_PHOSPHATE]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.ALKALINE_PHOSPHATE_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.ASPARTATE_AMINOTRANSFERASE]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.ASPARTATE_AMINOTRANSFERASE_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.CALCIUM]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.CALCIUM_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.LACTATE]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.LACTATE_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.SODIUM]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.SODIUM_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.TOTAL_BILIRUBIN]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.TOTAL_BILIRUBIN_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.TOTAL_PROTEIN]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.TOTAL_PROTEIN_DELTA
      }
    ]
  },
  [ALGORITHM_INPUTS.PHOSPHATE]: {
    label: 'Subcomponent',
    groups: [
      {
        label: 'Change from prior',
        value: ALGORITHM_INPUTS.PHOSPHATE_DELTA
      }
    ]
  }
};
